import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatFull } from "@/lib/format";
import { CommissionBadge } from "@/components/Badges";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const SUMMARY = [
  { key: "Estimée", labelKey: "estimated" },
  { key: "Confirmée", labelKey: "confirmed" },
  { key: "Facturée", labelKey: "invoiced" },
  { key: "Payée", labelKey: "paid" },
];

export default function Commissions() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/commissions").then((r) => setItems(r.data));
  }, []);

  const totalsByStatus = (status) =>
    items.filter((c) => c.status === status).length;

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-12" data-testid="commissions-page">
      <header className="mb-8">
        <p className="overline text-[#C6A87C]">{t("commissions.overline")}</p>
        <h1 className="mt-2 font-display text-4xl tracking-tight text-[#0F172A] sm:text-5xl">{t("commissions.title")}</h1>
        <p className="mt-2 text-slate-500">{t("commissions.subtitle")}</p>
      </header>

      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {SUMMARY.map((s) => (
          <div key={s.key} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`summary-${s.key}`}>
            <p className="overline text-slate-400">{t(`commissions.summary.${s.labelKey}`)}</p>
            <p className="mt-2 font-display text-3xl text-[#0F172A]">{totalsByStatus(s.key)}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
              <TableHead className="text-slate-500">{t("commissions.table.opportunity")}</TableHead>
              <TableHead className="text-slate-500">{t("commissions.table.percent")}</TableHead>
              <TableHead className="text-slate-500">{t("commissions.table.share")}</TableHead>
              <TableHead className="text-right text-slate-500">{t("commissions.table.amount")}</TableHead>
              <TableHead className="text-slate-500">{t("commissions.table.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c) => (
              <TableRow key={c.id} data-testid={`commission-row-${c.id}`} className="hover:bg-slate-50/60">
                <TableCell>
                  <p className="font-medium text-[#0F172A]">{c.opportunity_title}</p>
                  <p className="text-xs text-slate-400">{t("commissions.table.base", { amount: formatFull(c.base_amount, c.currency) })}</p>
                </TableCell>
                <TableCell className="text-slate-600">{c.percentage}%</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {c.partners.map((p, i) => (
                      <span key={i} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        {p.name} · {p.share}%
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-right font-display text-lg text-[#0F172A]">
                  {formatFull(c.amount, c.currency)}
                </TableCell>
                <TableCell>
                  <CommissionBadge status={c.status} testid={`commission-status-${c.id}`} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
